/**
 * Remove an account's offline store, coordinated across tabs.
 *
 * `deleteDatabase` blocks while any tab holds a connection, so the wipe
 * first announces `closing` (every tab closes), bumps the generation (every
 * in-flight write-through becomes void), deletes, then announces `wiped`
 * (every tab reloads). A browser-close logout cannot await IndexedDB, so it
 * sets a flag instead and the next boot wipes before anything opens.
 *
 * Every message carries the id of the tab that sent it. A BroadcastChannel
 * reaches every other channel object, including the ones the sending tab
 * holds, so without that tag the wiping tab would answer its own `wiped` by
 * reloading itself in the middle of signing out.
 */

import { bumpStoreGeneration, currentAccountId, lastAccountId } from './account';
import { resetOutboxState } from './outboxState';
import { closeStore, deleteStore } from './store';

export const OFFLINE_CHANNEL = 'pa-offline';
export const WIPE_PENDING_KEY = 'offline_wipe_pending';

type WipeMessage = { type: 'closing' | 'wiped'; account: string; from: string };

/** An id for this tab, so it can tell its own broadcasts from another tab's. */
function newTabId(): string {
  return globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2);
}

/**
 * This tab's id on the offline channel. One per tab, shared by every module
 * that posts there: a channel reaches the sender's own listeners too, and each
 * of them must be able to recognise its own tab's message.
 */
export const TAB_ID = newTabId();

/** A fresh channel for one post, or null in an environment without one (jsdom). */
function channel(): BroadcastChannel | null {
  return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(OFFLINE_CHANNEL);
}

/**
 * Wipe the signed-in account's store, or the last account's when signed out.
 *
 * Held under the flush lock, so a replay already in flight finishes against
 * the store it started in rather than having the database deleted underneath
 * it. A browser without Web Locks has no replay to wait for, so it wipes
 * straight away. The outbox state is reset once the rows are actually gone.
 */
export async function wipeOfflineStore(): Promise<void> {
  const account = currentAccountId() ?? lastAccountId();
  if (!account) return;
  const run = async (): Promise<void> => {
    const bc = channel();
    const post = (type: WipeMessage['type']) => bc?.postMessage({ type, account, from: TAB_ID });
    post('closing');
    await closeStore();
    bumpStoreGeneration(account);
    await deleteStore(account);
    resetOutboxState();
    post('wiped');
    bc?.close();
  };
  if (typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function') {
    await navigator.locks.request('pa-offline-flush', run);
    return;
  }
  await run();
}

/** Ask the next boot to wipe; for handlers that cannot await (beforeunload). */
export function markWipePending(): void {
  const account = currentAccountId() ?? lastAccountId();
  if (!account) return;
  try {
    localStorage.setItem(WIPE_PENDING_KEY, account);
  } catch {
    // No storage, no store to wipe.
  }
}

/**
 * Honour a pending wipe before anything opens the store. Call once at boot.
 *
 * The flag is cleared only once the database is actually gone: a delete that
 * fails leaves it set, so the next boot tries again rather than leaving the
 * account's data behind for good.
 */
export async function wipePendingOnBoot(): Promise<void> {
  let account: string | null = null;
  try {
    account = localStorage.getItem(WIPE_PENDING_KEY);
  } catch {
    return;
  }
  if (!account) return;
  bumpStoreGeneration(account);
  await deleteStore(account);
  try {
    localStorage.removeItem(WIPE_PENDING_KEY);
  } catch {
    // Storage vanished mid-boot; there is nothing left holding the flag.
  }
}

/**
 * React to another tab's wipe. Installed once at boot; returns a disposer.
 * `closing` releases our connection so the delete can proceed; `wiped`
 * reloads so no stale in-memory state outlives the store. This tab's own
 * messages are ignored — it has already done both.
 *
 * `reload` defaults to reloading the page and exists to be substituted in
 * tests: jsdom's `location` is [Unforgeable], so a real reload can be neither
 * stubbed nor observed there.
 */
export function listenForWipes(reload: () => void = () => window.location.reload()): () => void {
  const bc = channel();
  if (!bc) return () => undefined;
  bc.onmessage = (event: MessageEvent<WipeMessage>) => {
    const message = event.data;
    if (!message || message.from === TAB_ID) return;
    if (message.type === 'closing') void closeStore();
    if (message.type === 'wiped') reload();
  };
  return () => bc.close();
}
